'use client';

import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { AnimatePresence, motion } from 'motion/react';
import { Bot, Calendar, Mail, Menu, MessageSquare, Plus, Send, X } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useCallback } from 'react';

import { Button } from '@/core/ui/button';
import { Input } from '@/core/ui/input';
import { ScrollArea } from '@/core/ui/scroll-area';
import { cn } from '@/core/utils/utils';
import { submitInsight } from '@/modules/lab/chatbot/actions/insights';
import { AssistantMessage } from '@/modules/lab/chatbot/components/AssistantMessage';

interface ChatSession {
    id: string;
    title: string;
    updatedAt: number;
}


function getMessageText(message: { parts?: Array<{ type: string; text?: string }> }): string {
    if (!message.parts) return '';
    return message.parts
        .filter((part) => part.type === 'text' && 'text' in part)
        .map((part) => part.text)
        .join('');
}

function _getVisitorId(): string {
    if (typeof window === 'undefined') return '';
    let id = localStorage.getItem('chat_visitor_id');
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem('chat_visitor_id', id);
    }
    return id;
}

function ChatContent() {
    const searchParams = useSearchParams();
    const initialSessionId = searchParams?.get('session') || '';

    const [sessionId, setSessionId] = useState<string>(initialSessionId || '');
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [inputValue, setInputValue] = useState('');

    // Use useSyncExternalStore to safely get visitor ID from localStorage
    const subscribeToStorage = (callback: () => void) => {
        window.addEventListener('storage', callback);
        return () => window.removeEventListener('storage', callback);
    };
    const getVisitorIdSnapshot = () => {
        if (typeof window === 'undefined') return '';
        let id = localStorage.getItem('chat_visitor_id');
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem('chat_visitor_id', id);
        }
        return id;
    };
    const getServerSnapshot = () => '';
    const visitorId = useSyncExternalStore(subscribeToStorage, getVisitorIdSnapshot, getServerSnapshot);
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Load or create session
    useEffect(() => {
        if (!visitorId) return;

        const initSession = async () => {
            // Load existing sessions
            const res = await fetch(`/api/chat/sessions?visitorId=${visitorId}`);
            const data = await res.json();
            setSessions(data.sessions || []);

            // If no session specified, create new one
            if (!sessionId) {
                const newSessionId = crypto.randomUUID();
                await fetch('/api/chat/sessions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ visitorId, sessionId: newSessionId }),
                });
                setSessionId(newSessionId);
            }
        };

        initSession();
    }, [visitorId, sessionId]);

    const transport = useMemo(() => new DefaultChatTransport({ api: '/api/chat' }), []);

    const { messages, sendMessage, status, error, setMessages } = useChat({
        transport,
        onError: (err) => console.error("Chat error:", err),
    });

    const isLoading = status === 'streaming' || status === 'submitted';

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Focus input
    useEffect(() => {
        inputRef.current?.focus();
    }, [sessionId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || isLoading) return;

        await sendMessage({ text: inputValue });
        setInputValue('');

        // Persist to backend
        await fetch('/api/chat/history', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, role: 'user', content: inputValue }),
        });
    };

    const handleNewChat = async () => {
        const newSessionId = crypto.randomUUID();
        await fetch('/api/chat/sessions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ visitorId, sessionId: newSessionId }),
        });
        setSessionId(newSessionId);
        setMessages([]);

        // Refresh sessions
        const res = await fetch(`/api/chat/sessions?visitorId=${visitorId}`);
        const data = await res.json();
        setSessions(data.sessions || []);
    };

    const handleSelectSession = async (id: string) => {
        setSessionId(id);
        // Load history
        const res = await fetch(`/api/chat/history?sessionId=${id}`);
        const _data = await res.json();
        // Note: This requires adapting messages to UIMessage format
        // For now, we start fresh but the history is preserved in backend
        setMessages([]);
    };

    // Handle insight collection submission
    const handleCollectionSubmit = useCallback(async (
        answer: string | string[] | number,
        question: string,
        collectionType: string
    ) => {
        try {
            await submitInsight({
                visitorId,
                sessionId,
                industry: 'tech_solutions', // Default for basic chat
                type: collectionType as 'tag' | 'choice' | 'scale' | 'input',
                question,
                answer,
                queryCount: messages.length,
                lastTopic: messages[messages.length - 1]?.role === 'user'
                    ? getMessageText(messages[messages.length - 1])
                    : undefined,
            });
        } catch (err) {
            console.error('Failed to submit insight:', err);
        }
    }, [visitorId, sessionId, messages]);

    // Handle lead capture submission
    const handleContactSubmit = useCallback(async (contactType: string, contactValue: string) => {
        try {
            await fetch('/api/leads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    visitorId,
                    sessionId,
                    contactType,
                    contactValue,
                    leadScore: 50,
                    intentLevel: 'interested',
                    conversationSummary: messages.map(m => getMessageText(m)).join('\n'),
                    interestedServices: [],
                    industry: 'tech_solutions',
                }),
            });
        } catch (err) {
            console.error('Failed to capture lead:', err);
        }
    }, [visitorId, sessionId, messages]);

    return (
        <div className="flex h-screen bg-background">
            {/* Sidebar */}
            <AnimatePresence>
                {sidebarOpen && (
                    <motion.aside
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: 280, opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-r border-border/50 bg-muted/30 flex flex-col overflow-hidden"
                    >
                        <div className="p-4 border-b border-border/50">
                            <Button onClick={handleNewChat} className="w-full justify-start gap-2" variant="outline">
                                <Plus className="w-4 h-4" />
                                New Chat
                            </Button>
                        </div>

                        <ScrollArea className="flex-1 p-2">
                            <div className="space-y-1">
                                {sessions.map((s) => (
                                    <button
                                        key={s.id}
                                        onClick={() => handleSelectSession(s.id)}
                                        className={cn(
                                            "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                                            s.id === sessionId
                                                ? "bg-primary/10 text-primary"
                                                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                                        )}
                                    >
                                        <div className="flex items-center gap-2">
                                            <MessageSquare className="w-4 h-4 shrink-0" />
                                            <span className="truncate">{s.title}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </ScrollArea>

                        {/* Quick Actions */}
                        <div className="p-3 border-t border-border/50 space-y-2">
                            <Button
                                variant="ghost"
                                className="w-full justify-start gap-2 text-sm"
                                onClick={() => window.open('https://cal.com/hamid-ayub/discovery', '_blank')}
                            >
                                <Calendar className="w-4 h-4" />
                                Book a Call
                            </Button>
                            <Button
                                variant="ghost"
                                className="w-full justify-start gap-2 text-sm"
                                onClick={() => window.location.href = '/contact'}
                            >
                                <Mail className="w-4 h-4" />
                                Submit Query
                            </Button>
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <header className="h-14 border-b border-border/50 flex items-center px-4 gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </Button>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Bot className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-sm font-semibold">Principal Assistant</h1>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">AI Powered</p>
                        </div>
                    </div>
                </header>

                {/* Messages */}
                <ScrollArea className="flex-1 p-6">
                    <div className="max-w-3xl mx-auto space-y-6">
                        {messages.length === 0 && (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <Bot className="w-8 h-8 text-primary" />
                                </div>
                                <h2 className="text-xl font-semibold mb-2">How can I help?</h2>
                                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                                    Ask about our services, engineering approach, or technical challenges.
                                </p>
                            </div>
                        )}

                        {error && (
                            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive">
                                Something went wrong. Please try again.
                            </div>
                        )}

                        {messages.map((m, idx) => {
                            const text = getMessageText(m);
                            if (!text) return null;

                            const isUser = m.role === 'user';
                            const isMessageStreaming = status === 'streaming' && idx === messages.length - 1 && !isUser;

                            if (isUser) {
                                return (
                                    <motion.div
                                        key={m.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex gap-4 justify-end"
                                    >
                                        <div className="max-w-2xl px-5 py-3 rounded-2xl text-[15px] leading-relaxed bg-primary text-primary-foreground rounded-tr-md">
                                            {text}
                                        </div>
                                    </motion.div>
                                );
                            }

                            return (
                                <AssistantMessage
                                    key={m.id}
                                    messageId={m.id}
                                    text={text}
                                    isStreaming={isMessageStreaming}
                                    isExpanded={false}
                                    onCollectionSubmit={handleCollectionSubmit}
                                    onCTAContactSubmit={handleContactSubmit}
                                />
                            );
                        })}

                        {isLoading && (
                            <div className="flex gap-4">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                    <Bot className="w-4 h-4 text-primary" />
                                </div>
                                <div className="bg-muted/50 border border-border/50 rounded-2xl rounded-tl-md px-5 py-4 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" />
                                    <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        )}

                        <div ref={scrollRef} />
                    </div>
                </ScrollArea>

                {/* Input */}
                <div className="border-t border-border/50 p-4">
                    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto relative">
                        <Input
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Message Principal Assistant..."
                            className="pr-14 h-12 bg-muted/30 border-border/50 rounded-xl text-[15px]"
                            disabled={isLoading}
                        />
                        <Button
                            type="submit"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg"
                            disabled={isLoading || !inputValue.trim()}
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </form>
                    <p className="text-center text-[11px] text-muted-foreground/60 mt-2">
                        Your conversation is private and secure.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function ChatPage() {
    return (
        <Suspense fallback={null}>
            <ChatContent />
        </Suspense>
    );
}
